<script>
// Faixa de 4 KPIs do Laboratório fee-based: economia do cliente, ganho anual de
// quem atende, cruzamento e Δ patrimônio do cliente.
//
// ★ Os três KPIs de valor falam do ANO FINAL do horizonte (owner, 2026-08-03).
// A faixa misturava períodos — economia e Δ patrimônio acumulados dos 5 anos,
// ganho como fluxo de um ano —, e quem lia os quatro juntos somava grandezas
// de escalas diferentes. O cruzamento é a exceção declarada pelo owner: é um
// ano-índice, não um fluxo, e continua como está.
//
// ★ TUDO nesta faixa é do canal do cenário — nome e número. Quem atende é
// nomeado por `termoDoProfissional`; o ganho e o Δ patrimônio saem da PERNA que
// `regimeDoCanal` escolhe (`fee` | `cons`). Ler a perna nomeada em vez da perna
// do canal fica verde enquanto o default coincide e erra calado quando alguém
// troca o seletor: era o que fazia o KPI anunciar R$ 1 mil de ganho com o
// Consultor CVM selecionado, ao lado de um card publicando 60 bps de receita.

import {
  fmtBRLk,
  signPctF,
  calcularGanhoAnualDoProfissional,
} from '@modules/fee-fixo/utils/fee-lab-format'
import {
  CANAL_PADRAO,
  regimeDoCanal,
  termoDoProfissional,
} from '@modules/fee-fixo/constants/baldes'
import { numOr0, toneBySign } from '@modules/fee-fixo/utils/Utils'

// Sem receita no comissionado não há crescimento a declarar. A divisão crua
// daria `Infinity` e o guard do motor devolve 0 — que exibiria "+0%"
// justamente no cenário em que a receita saiu do zero. As duas saídas mentem.
const SEM_BASE_DE_COMPARACAO = 'sem receita no comissionado para comparar'

// Como o KPI de cruzamento NOMEIA o regime que está sendo comparado. O valor já
// seguia o canal desde a [D8] (`cross` sai de `series[regimeDoCanal(canal)]`);
// o rótulo dizia "fee ✕ comis" nos dois, e na consultoria anunciava uma
// comparação diferente da que estava sendo feita. `fee`/`cons`/`comis` são
// chaves de regime do MOTOR — não vocabulário de tela.
const NOME_CURTO_DO_REGIME = { fee: 'fee fixo', cons: 'consultoria' }
const NOME_CURTO_DO_COMISSIONADO = 'comissionado'

export default {
  name: 'FeeLabKpiStrip',
  props: {
    resultado: {
      type: Object,
      default: null,
    },
    auc: {
      type: Number,
      default: 0,
    },
    // O modelo de atendimento do cenário — o mesmo `state.canal` que comanda
    // os cards de regime. Ausente, cai no default declarado ([D6]).
    canal: {
      type: String,
      default: CANAL_PADRAO,
    },
  },
  data() {
    return {
      // Cor semântica do valor por sinal (token, não hex). Fora do az-text
      // porque o sistema não modela success/danger de texto.
      toneStyles: {
        pos: { color: 'var(--success)' },
        neg: { color: 'var(--danger)' },
      },
    }
  },
  computed: {
    /**
     * A perna que o canal do cenário escolhe (`fee` | `cons`) — a MESMA chave
     * que `adv`, `advGain` e `patrGain` usam no resultado.
     *
     * Resolvida uma vez aqui porque cada KPI que repetisse a escolha é mais um
     * lugar onde a faixa pode voltar a falar de dois regimes ao mesmo tempo —
     * que é exatamente o defeito que o owner viu: o valor do ganho saía da
     * perna do fee enquanto o card ao lado publicava a receita do consultor.
     */
    perna() {
      return regimeDoCanal(this.canal)
    },
    /**
     * O crescimento da receita contra o comissionado, em % (owner,
     * 2026-08-03: *"colocar no card o crescimento da receita %"*).
     *
     * Sai de `pctAdv` — o motor já publica a razão, por perna. Refazer a
     * divisão aqui seria uma segunda fonte para o mesmo número; o que a tela
     * decide é só se a comparação existe, olhando o denominador.
     *
     * No cenário default o consultor sai de 10,72 bps (R$ 1.071,91/ano) para
     * 60 bps (R$ 6.000/ano): +460%.
     */
    crescimentoDaReceita() {
      const r = this.resultado
      if (!r || !numOr0(r.adv, 'comis')) return SEM_BASE_DE_COMPARACAO
      return signPctF(numOr0(r.pctAdv, this.perna)) + ' vs o comissionado'
    },
    kpis() {
      const r = this.resultado
      if (!r) return []
      // O ano do horizonte, que nomeia os três KPIs de valor. Sai de `r.H`
      // (1, 3 ou 5, conforme o seletor de premissas) — um "ano 5" fixo seria a
      // mesma classe de erro que a troca de período veio corrigir.
      const anoFinal = ' · ano ' + r.H
      const save = numOr0(r.save, 'clienteAnoFinal')
      const advYear = calcularGanhoAnualDoProfissional(r, this.auc, this.canal)
      const patr = numOr0(r.patrGain, this.perna)
      return [
        {
          key: 'save',
          label: 'Economia do cliente' + anoFinal,
          value: fmtBRLk(save),
          // ★ [G-1] STRESS rodada 1 (07/09/2026) — o percentual é a
          // variação do custo do cliente contra o CUSTO COMISSIONADO
          // daquele mesmo ano (`pctSave.clienteAnoFinal`, fee-lab-engine.js),
          // nunca uma fração do patrimônio: a mesma alta de custo, medida
          // sobre o patrimônio (ordens de magnitude maior), sai ~100x menor.
          // O rótulo dizia "sobre o patrimônio daquele ano" e o app exibia
          // −10,7% num cliente cujo custo subiu de 0,84% a.a. para 0,93%
          // a.a. — sobre o patrimônio daquele ano (~R$ 19,9 mi) seria
          // −0,08%. Duas ordens de grandeza na frente do cliente.
          //
          // A BASE do valor em REAIS (não do percentual) continua com nota
          // própria: `save` é medido sobre o patrimônio já capitalizado até
          // aquele ano, e o rodapé do card de destino aplica os bps sobre o
          // AuC de HOJE (ele declara "sobre R$ X mi"). Os dois estão certos
          // e diziam "ano N" sem dizer sobre o quê — no cliente medido,
          // R$ 162 mil aqui contra R$ 119.337 lá.
          hint:
            signPctF(numOr0(r.pctSave, 'clienteAnoFinal')) +
            ' · sobre o custo comissionado daquele ano',
          tone: toneBySign(save),
        },
        {
          key: 'adv',
          label: 'Ganho do ' + termoDoProfissional(this.canal) + anoFinal,
          value: fmtBRLk(advYear),
          // A comparação em bps saiu daqui: as duas receitas continuam na
          // tela, em % a.a., nos cards de regime logo acima — e é a
          // percentagem que se diz numa conversa com o cliente.
          hint: this.crescimentoDaReceita,
          tone: toneBySign(advYear),
        },
        {
          key: 'cross',
          // Lê a MESMA `perna` do resto da faixa: o rótulo nomeia o regime que
          // o `cross` de fato comparou.
          label:
            'Cruzamento (' +
            NOME_CURTO_DO_REGIME[this.perna] +
            ' ✕ ' +
            NOME_CURTO_DO_COMISSIONADO +
            ')',
          value: r.cross ? 'Ano ' + r.cross : 'Não cruza',
          // A dica dizia "o fee fica mais barato" — o mesmo vazamento, e com
          // concordância impossível de manter nos dois canais. O fato é o
          // mesmo nos dois: a partir daquele ano o cliente paga menos.
          hint: r.cross
            ? 'a partir dele o cliente paga menos que hoje'
            : 'ajuste migração ou fee',
          tone: r.cross ? 'pos' : 'neg',
        },
        {
          key: 'patr',
          // Estoque ao FIM do horizonte: já era "no ano N" por natureza, e por
          // isso só o rótulo mudou. O valor continua o mesmo.
          label: 'Δ patrimônio cliente' + anoFinal,
          value: fmtBRLk(patr),
          hint: 'custo + alfa do serviço capitalizados',
          tone: toneBySign(patr),
        },
      ]
    },
  },
}
</script>

<template>
  <div class="kpi-strip">
    <div
      v-for="kpi in kpis"
      :key="kpi.key"
      class="kpi-strip__card az-card-bordered az-gap-xsmall"
    >
      <span class="az-text-small">{{ kpi.label }}</span>
      <span class="az-title-secondary" :style="toneStyles[kpi.tone]">
        {{ kpi.value }}
      </span>
      <span class="az-text-small" aria-live="polite">{{ kpi.hint }}</span>
    </div>
  </div>
</template>

<style lang="sass" scoped>
.kpi-strip
  display: grid
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr))
  gap: 12px

.kpi-strip__card
  display: flex
  flex-direction: column
</style>
